import { useMemo } from 'react';
import { goBack } from '../../app/navigation';
import { AI_JOB_GROUP_ORDER, type AiJobGroup, type V2AiJob } from '../../domain/aiJob';
import { displayAddress } from '../../domain/inspection';
import { useT, type MessageKey } from '../../i18n/i18n';
import { aiFailureMessage } from '../../i18n/labels';
import { aiJobsStore, jobCountsOf, jobsInGroup } from '../../state/aiJobsStore';
import { useStore } from '../../state/createStore';
import { inspectionStore } from '../../state/inspectionStore';
import { EmptyState, ListRow, SectionEyebrow, TopBar } from '../../ui/components';
import { formatClock } from '../../ui/hooks';
import { advancedStatusLabel } from './AdvancedReviewBlock';
import { openAdvancedResult } from './openAdvancedResult';

// AI 任务 (Owner contract 2026-09-13 §12): every advanced review of one inspection (or of the
// account), in four groups — 分析中 · 已完成未查看 · 已查看 · 失败 / 需要处理 — each row one tap
// from the item it belongs to. A list, not a dashboard.
const GROUP_KEYS: Record<AiJobGroup, MessageKey> = Object.freeze({
  active: 'aiTasks.group.active',
  unread: 'aiTasks.group.unread',
  viewed: 'aiTasks.group.viewed',
  failed: 'aiTasks.group.failed'
});

const GROUP_ICON: Record<AiJobGroup, 'psychology' | 'check_circle' | 'done_all' | 'error'> = Object.freeze({
  active: 'psychology',
  unread: 'check_circle',
  viewed: 'done_all',
  failed: 'error'
});

export function AiTaskCenterScreen(props: { inspectionId: string | null }) {
  const { t } = useT();
  const jobsState = useStore(aiJobsStore, (s) => s);
  const inspections = useStore(inspectionStore, (s) => s.inspections);
  const scope = props.inspectionId;
  const inspection = scope ? (inspections.find((i) => i.id === scope) ?? null) : null;
  const counts = useMemo(() => jobCountsOf(jobsState, scope), [jobsState, scope]);
  const groups = useMemo(() => AI_JOB_GROUP_ORDER.map((group) => ({ group, jobs: jobsInGroup(jobsState, group, scope) })), [jobsState, scope]);
  const total = groups.reduce((acc, g) => acc + g.jobs.length, 0);

  function subtitleOf(job: V2AiJob): string {
    const where = scope ? null : (inspections.find((i) => i.id === job.inspectionId) ?? null);
    const parts = [where ? displayAddress(where, t('inspection.noAddress')) : null, job.subject.areaName, job.subject.itemTitle].filter((s): s is string => typeof s === 'string' && s.length > 0);
    return parts.join(' · ');
  }

  function metaOf(job: V2AiJob): string {
    const at = job.completedAt ?? job.updatedAt;
    const status = advancedStatusLabel(t, job.status);
    const reason = job.errorCode && !job.result ? aiFailureMessage(t, job.errorCode) : null;
    return [status, reason, at > 0 ? formatClock(at) : null].filter((s): s is string => typeof s === 'string' && s.length > 0).join(' · ');
  }

  return (
    <div className="screen" data-testid="ai-tasks-screen">
      <TopBar title={t('aiTasks.title')} subtitle={inspection ? displayAddress(inspection, t('inspection.noAddress')) : t('aiTasks.subtitleAll')} onBack={goBack} />
      <div className="screen-body">
        <p className="card-note aitasks-counts" data-testid="ai-tasks-counts" data-active={counts.active} data-unread={counts.unread} data-failed={counts.failed}>
          {t('aiTasks.counts', { active: counts.active, unread: counts.unread })}
        </p>
        {total === 0 ? (
          <EmptyState title={t('aiTasks.title')} body={t('aiTasks.empty')} />
        ) : (
          groups
            .filter((g) => g.jobs.length > 0)
            .map((g) => (
              <section key={g.group} className="list aitasks-group" data-testid={`ai-tasks-group-${g.group}`} data-count={g.jobs.length}>
                <SectionEyebrow className="settings-group-label">{`${t(GROUP_KEYS[g.group])} · ${g.jobs.length}`}</SectionEyebrow>
                {g.jobs.map((job) => (
                  <ListRow
                    key={job.id}
                    icon={GROUP_ICON[g.group]}
                    iconTone={g.group === 'failed' ? 'error' : g.group === 'unread' ? 'default' : 'soft'}
                    title={job.subject.itemTitle ? `${job.subject.areaName} · ${job.subject.itemTitle}` : job.subject.areaName}
                    subtitle={`${subtitleOf(job)}${scope ? '' : ''} — ${metaOf(job)}`}
                    trailing={g.group === 'unread' ? <span className="badge badge-warn">{t('aiTasks.group.unread')}</span> : undefined}
                    rail={g.group === 'failed' ? 'error' : g.group === 'unread' ? 'primary' : undefined}
                    chevron
                    onClick={() => openAdvancedResult(job)}
                    dataName={job.id}
                    testId="ai-task-row"
                  />
                ))}
              </section>
            ))
        )}
      </div>
    </div>
  );
}
